/*
 * AdminCatalog — Phase CC-1
 * Platform-wide catalog browser with seller filter, force-edit, bulk-pause.
 */
import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Package, Pencil, PauseCircle, PlayCircle, Search, Loader2, History,
} from "lucide-react";


export default function AdminCatalog() {
  const [q, setQ] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [activeFilter, setActiveFilter] = useState("");  // "" | "true" | "false"
  const [data, setData] = useState({ rows: [], total: 0, paused: 0, sellers_count: 0 });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (sellerId) params.set("seller_id", sellerId);
      if (activeFilter) params.set("active", activeFilter);
      params.set("limit", "100");
      const r = await api.get(`/admin/catalog?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load catalog");
    } finally { setBusy(false); }
  }, [q, sellerId, activeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-catalog">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Catalog Oversight</h1>
        <p className="text-[12.5px] text-foreground/55">
          Browse every product on the platform · force-edit price/MOQ · bulk-pause by seller.
        </p>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi label="Products" value={data.total} icon={Package}/>
        <Kpi label="Paused" value={data.paused} tone={data.paused > 0 ? "amber" : "neutral"}/>
        <Kpi label="Sellers (in view)" value={data.sellers_count}/>
        <Kpi label="Active" value={Math.max(0, data.total - data.paused)} tone="emerald"/>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="catalog-q">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-foreground/40"/>
            <Input id="catalog-q" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Name · SKU · UPC" className="pl-8"
              data-testid="catalog-search"/>
          </div>
        </div>
        <div className="min-w-[200px]">
          <Label htmlFor="catalog-seller">Seller ID</Label>
          <Input id="catalog-seller" value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            placeholder="Optional" data-testid="catalog-seller-filter"/>
        </div>
        <div className="min-w-[140px]">
          <Label htmlFor="catalog-active">Status</Label>
          <select id="catalog-active" value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="w-full h-9 px-2 rounded-md border bg-background text-[13px]"
            data-testid="catalog-active-filter">
            <option value="">Any</option>
            <option value="true">Active</option>
            <option value="false">Paused</option>
          </select>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="catalog-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
        {sellerId && (
          <Button variant="outline" onClick={() => setBulkOpen(true)}
            data-testid="catalog-bulk-pause-btn">
            <PauseCircle className="w-3.5 h-3.5 mr-1"/>Bulk pause seller
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="catalog-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Seller</th>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-right">MOQ</th>
              <th className="px-3 py-2 text-right">Stock</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`catalog-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.name || r.id}</td>
                <td className="px-3 py-2 text-foreground/65">{r.seller?.email || r.seller_id || "—"}</td>
                <td className="px-3 py-2 mono text-[11.5px]">{r.sku || "—"}</td>
                <td className="px-3 py-2 text-right mono">${(r.price ?? 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-right">{r.moq ?? "—"}</td>
                <td className="px-3 py-2 text-right">{r.stock ?? "—"}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${
                    r.active ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                              : "bg-amber-50 border-amber-200 text-amber-800"
                  }`}>{r.active ? "Active" : "Paused"}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setSelected(r)}
                    data-testid={`catalog-edit-${r.id}`}>
                    <Pencil className="w-3 h-3 mr-1"/>Edit
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-foreground/45">No products found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <EditProductDialog product={selected} onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); load(); }}/>

      <BulkPauseDialog open={bulkOpen} sellerId={sellerId}
        onClose={() => setBulkOpen(false)}
        onSaved={() => { setBulkOpen(false); load(); }}/>
    </div>
  );
}


function Kpi({ label, value, icon: Icon = Package, tone = "neutral" }) {
  const tones = {
    neutral: "bg-foreground/3 border-foreground/10",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between text-[11px] text-foreground/55 uppercase tracking-wider">
        <span>{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5"/>}
      </div>
      <div className="text-2xl font-semibold mt-1">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}


function EditProductDialog({ product, onClose, onSaved }) {
  const [state, setState] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!product;

  useEffect(() => {
    if (product) {
      setState({
        name: product.name || "",
        price: product.price ?? 0,
        moq: product.moq ?? 0,
        stock: product.stock ?? 0,
        retail_price: product.retail_price ?? 0,
        active: !!product.active,
      });
      setReason("");
    }
  }, [product]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars · audit-logged)");
      return;
    }
    setBusy(true);
    try {
      const payload = { reason: reason.trim() };
      ["name", "price", "moq", "stock", "retail_price", "active"].forEach(k => {
        if (state[k] !== undefined && state[k] !== "") payload[k] = state[k];
      });
      const r = await api.patch(`/admin/catalog/${product.id}`, payload);
      toast.success(`Saved · override #${(r.data.override_id || "").slice(0,8)}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="catalog-edit-dialog">
        <DialogHeader>
          <DialogTitle>Force-edit product</DialogTitle>
          <DialogDescription>
            Every field change is audit-logged. Owner: <span className="mono">{product?.seller_id || "—"}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Name" id="ce-name" value={state.name} onChange={v => setState({...state, name: v})}/>
            <Field label="SKU (read-only)" id="ce-sku" value={product?.sku} disabled/>
            <Field label="Price ($)" id="ce-price" type="number" value={state.price}
              onChange={v => setState({...state, price: Number(v)})}/>
            <Field label="MOQ" id="ce-moq" type="number" value={state.moq}
              onChange={v => setState({...state, moq: Number(v)})}/>
            <Field label="Stock" id="ce-stock" type="number" value={state.stock}
              onChange={v => setState({...state, stock: Number(v)})}/>
            <Field label="Retail price ($)" id="ce-retail" type="number" value={state.retail_price}
              onChange={v => setState({...state, retail_price: Number(v)})}/>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input type="checkbox" id="ce-active" checked={!!state.active}
              onChange={(e) => setState({...state, active: e.target.checked})}
              data-testid="catalog-edit-active"/>
            <label htmlFor="ce-active" className="text-[12.5px]">Product is active (listed)</label>
          </div>
          <div className="pt-2">
            <Label htmlFor="ce-reason">Reason (min 3 chars · audit-logged)</Label>
            <Textarea id="ce-reason" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="catalog-edit-reason"
              placeholder="e.g. seller flagged for predatory pricing"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}
            data-testid="catalog-edit-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save & audit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BulkPauseDialog({ open, sellerId, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [paused, setPaused] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars)");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/catalog/bulk-pause`, {
        seller_id: sellerId, reason: reason.trim(), paused,
      });
      toast.success(`${r.data.affected} products ${paused ? "paused" : "unpaused"}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="catalog-bulk-pause-dialog">
        <DialogHeader>
          <DialogTitle>{paused ? "Pause" : "Unpause"} all products for seller</DialogTitle>
          <DialogDescription>
            Composite action — single audit row capturing the affected count. Seller ID: <span className="mono">{sellerId}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex gap-2">
            <Button variant={paused ? "default" : "outline"} onClick={() => setPaused(true)}>
              <PauseCircle className="w-3.5 h-3.5 mr-1"/>Pause
            </Button>
            <Button variant={!paused ? "default" : "outline"} onClick={() => setPaused(false)}>
              <PlayCircle className="w-3.5 h-3.5 mr-1"/>Unpause
            </Button>
          </div>
          <div>
            <Label htmlFor="bp-reason">Reason (min 3 chars · audit-logged)</Label>
            <Textarea id="bp-reason" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="catalog-bulk-reason"
              placeholder="e.g. seller account compromised — pause until ownership confirmed"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}
            data-testid="catalog-bulk-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}
            Apply to all products
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, id, value, onChange, type = "text", disabled = false }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}/>
    </div>
  );
}
